































































import { Vue, Component, Prop } from "vue-property-decorator";
import { ProgramChain, getEndDateOfProgramChain } from "@/data/models/ProgramChain";
import { firestore } from "firebase/app";

interface StepEntry {
  value: number;
  label: string;
  timestampTooltip: string;
  /** Percentage position within the visible range (only set when zoomed). */
  percent: number;
}

@Component
export default class DryingTimelineMarker extends Vue {
  // ─── Props ───────────────────────────────────────────────────────────

  @Prop({ required: true })
  programChain!: ProgramChain;

  @Prop({ default: "" })
  modelVersion!: string;

  @Prop({ default: true })
  visible!: boolean;

  /**
   * Chart area left/right pixel offsets from the DryingProgressGraph.
   * Used to align the radio strip with the graph x-axis.
   */
  @Prop({ default: () => ({ left: 0, right: 0, width: 0 }) })
  chartArea!: { left: number; right: number; width: number };

  /** Visible x-axis range from the graph (null when not available). */
  @Prop({ default: null }) visibleRange!: {
    xMin: number; xMax: number;
    startDate: Date;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timeScale: any;
  } | null;

  /** Current stationary relative value (from parent). */
  @Prop({ default: null })
  stationaryValue!: number | null;

  /** Current dry relative value (from parent). */
  @Prop({ default: null })
  dryValue!: number | null;

  /** The timestamp of the last prediction (determines the 1.00 end point). */
  @Prop({ default: null })
  lastPredictionDate!: Date | null;

  // ─── Computed ────────────────────────────────────────────────────────

  /** True when chart area pixel values have been received. */
  get isChartAreaReady(): boolean {
    return !!this.chartArea && this.chartArea.width > 0;
  }

  /** Whether the component should be shown (always visible — zoom is handled by positioning). */
  get showMarker(): boolean {
    return this.visible && this.showStatusRadioRows;
  }

  /** Stat./Trock. radios are hidden for RENO-family model versions. */
  get showStatusRadioRows(): boolean {
    return !this.modelVersion?.startsWith("R");
  }

  /** Whether we have axis range info and should use absolute positioning. */
  get isPositioned(): boolean {
    return !!this.visibleRange;
  }

  /** Whether the view is zoomed in (some steps are outside visible range). */
  get isZoomed(): boolean {
    return this.isPositioned && this.visibleSteps.length < this.steps.length;
  }

  /** 21 steps from 0.00 to 1.00 in increments of 0.05, with timestamp tooltips */
  get steps(): StepEntry[] {
    const result: StepEntry[] = [];
    for (let i = 0; i <= 20; i++) {
      const value = Math.round(i * 5) / 100; // 0.00, 0.05, ..., 1.00
      result.push({
        value,
        label: value.toFixed(2),
        timestampTooltip: this.formatTimestamp(value),
        percent: 0,
      });
    }
    return result;
  }

  /**
   * Converts a step's relative value (0.00–1.00) to the graph's x-axis value
   * using the same coordinate system as DryingProgressGraph.
   */
  private stepToXValue(relativeValue: number): number {
    if (!this.visibleRange) return 0;
    const { startDate, timeScale } = this.visibleRange;
    const refMidnight = new Date(startDate);
    refMidnight.setHours(0, 0, 0, 0);
    const stepMs = this.startMs + (this.endMs - this.startMs) * relativeValue;
    const minutesFromMidnight = (stepMs - refMidnight.getTime()) / (1000 * 60);
    return timeScale.scaleMinutes(minutesFromMidnight);
  }

  /** Steps filtered and positioned within the visible x-axis range. */
  get visibleSteps(): StepEntry[] {
    if (!this.visibleRange) return this.steps;
    const { xMin, xMax } = this.visibleRange;
    const range = xMax - xMin;
    if (range <= 0) return this.steps;

    return this.steps
      .map(step => {
        const xVal = this.stepToXValue(step.value);
        const percent = ((xVal - xMin) / range) * 100;
        return { ...step, percent };
      })
      .filter(step => step.percent >= -5 && step.percent <= 105);
  }

  get chainId(): string {
    return this.programChain?.id ?? "";
  }

  /**
   * Keep the German labels short so they remain readable in narrow left-axis space.
   */
  get stationaryLabel(): string {
    return this.$i18n.locale === "de"
      ? "Stat."
      : this.$t("quality.timeline.stationary").toString();
  }

  get dryLabel(): string {
    return this.$i18n.locale === "de"
      ? "Trock."
      : this.$t("quality.timeline.dry").toString();
  }

  /** Start timestamp of the program chain in milliseconds */
  get startMs(): number {
    return this.programChain.startDate.toDate().getTime();
  }

  /** End timestamp: min(lastPredictionDate, programChainEndDate).
   *  If lastPredictionDate is within the chain, use it.
   *  If it's after chain end, cap at chain end.
   */
  get endMs(): number {
    const chainEnd = getEndDateOfProgramChain(this.programChain);
    const chainEndMs = chainEnd ? chainEnd.toDate().getTime() : this.startMs;

    if (this.lastPredictionDate) {
      const predMs = this.lastPredictionDate.getTime();
      return Math.min(predMs, chainEndMs);
    }
    return chainEndMs;
  }

  /**
   * CSS style that positions the label inside the left margin area
   * (overlapping the y-axis label space).
   */
  get labelStyle(): Record<string, string> {
    return {
      width: `${this.chartArea.left - 20}px`,
    };
  }

  /**
   * CSS style that aligns the radio strip with the chart area's x-axis.
   * Applies left margin and right margin matching the chart padding from y-axis labels.
   */
  get stripStyle(): Record<string, string> {
    const rightMargin = this.chartArea.width - this.chartArea.right;
    return {
      "margin-left": `${this.chartArea.left}px`,
      "margin-right": `${rightMargin}px`,
    };
  }

  // ─── Methods ─────────────────────────────────────────────────────────

  /**
   * Converts a relative value (0.00–1.00) to a Firebase Timestamp.
   */
  private relativeToTimestamp(relativeValue: number): firestore.Timestamp {
    const ms = this.startMs + (this.endMs - this.startMs) * relativeValue;
    return firestore.Timestamp.fromMillis(ms);
  }

  private emitState(field: "stationary" | "dry", relativeValue: number) {
    this.$emit("update", {
      field,
      relativeValue,
      timestamp: this.relativeToTimestamp(relativeValue),
    });
  }

  onStationaryChange(value: number) {
    this.emitState("stationary", value);
  }

  onDryChange(value: number) {
    this.emitState("dry", value);
  }

  /** Formats a relative value (0.00–1.00) as a human-readable date/time string. */
  private formatTimestamp(relativeValue: number): string {
    const ms = this.startMs + (this.endMs - this.startMs) * relativeValue;
    const date = new Date(ms);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}.${month}. ${hours}:${minutes}`;
  }
}
